<template>
  <v-card>
    <v-toolbar dark color="primary" tile flat class="mb-5">
      <v-btn icon dark @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title>{{ $t('absence.deleteTitle') }}</v-toolbar-title>
    </v-toolbar>

    <div v-for="(absence, index) in chatsAbsence" :key="`${index}-absence`" class="mb-5">
      <v-card class="mx-5">
        <v-card-title>
          {{ $t('common.date') }}：{{ formatAbsenceDate(absence.date) }}
        </v-card-title>
        <v-card-subtitle class="mt-2">
          {{ $t('absence.reason') }}
        </v-card-subtitle>
        <template v-if="absence.absenceDetails">
          <v-card-text style="white-space:pre-wrap;">
            {{ showReason(absence.absenceDetails.reason) }}
          </v-card-text>
          <template v-if="absence.absenceDetails.comment">
            <v-card-subtitle>
              {{ $t('absence.comment') }}
            </v-card-subtitle>
            <v-card-text style="white-space:pre-wrap;">
              {{ absence.absenceDetails.comment }}
            </v-card-text>
          </template>
        </template>

        <div class="d-flex justify-center pb-3">
          <v-card-actions>
            <v-btn color="error" depressed @click="isDeleteAbsence(absence)" variant="flat">
              {{ $t('absence.delete') }}
            </v-btn>
          </v-card-actions>
        </div>
        <v-divider></v-divider>
      </v-card>
    </div>

    <v-dialog v-model="deleteDialog" class="delete-absence-dialog">
      <v-card>
        <div class="d-flex justify-space-around pt-3 pb-3">
          <v-card-title class="headline lighten-2">{{ $t('common.deleteConfirm') }}</v-card-title>
        </div>
        <div class="d-flex justify-space-around pt-3 pb-3">
          <v-card-actions>
            <v-btn @click="() => (deleteDialog = false)" variant="text">
              {{ $t('common.cancel') }}
            </v-btn>
            <v-btn
              color="error"
              class="ml-5"
              depressed
              @click="deleteAbsence()"
              :loading="deleteLoading"
              variant="flat"
            >
              {{ $t('common.delete') }}
            </v-btn>
          </v-card-actions>
        </div>
      </v-card>
    </v-dialog>
  </v-card>
</template>
<script lang="ts">
import Vue, { PropType, defineComponent } from 'vue';
import dayjs from 'dayjs';
import rules from '@/service/ValidRules';

import { chatModule } from '@/store/dataModules/chatModule';
import { when } from '@/service/Switch';

// model
import { VForm } from '@/model/Vuetify/VForm';
import { AbsenceReason, AbsenceReasonType, NurseryAttendanceResponse } from 'chaild-api/lib';

interface DataType {
  deleteLoading: boolean;
  deleteDialog: boolean;
  absence: NurseryAttendanceResponse | null;
}

export default defineComponent({
  name: 'AbsenceModal',

  props: {
    chatsAbsence: {
      type: Array as PropType<NurseryAttendanceResponse[]>,
      default: [],
    },
    childId: {
      type: Number,
      default: NaN,
    },
    date: {
      type: Object as PropType<any>,
      default: {},
    },
    nurseryId: {
      type: Number,
      default: NaN,
    },
  },

  computed: {},

  data: (): DataType => ({
    deleteLoading: false,
    deleteDialog: false,
    absence: null,
  }),

  watch: {
    deleteDialog: {
      async handler(val: boolean) {
        if (!val) {
          this.absence = null;
        }
      },
      deep: true,
    },
  },

  methods: {
    formatAbsenceDate(date: string | undefined): string {
      return date ? dayjs(date).format('DD/MM/YYYY') : '';
    },
    showReason(reason: AbsenceReasonType | null): string {
      const temp = when(reason)
        .on(
          v => v === AbsenceReason.fever,
          () => (this as any).$t('absenceReason.fever')
        )
        .on(
          v => v === AbsenceReason.personal,
          () => (this as any).$t('absenceReason.personal')
        )
        .on(
          v => v === AbsenceReason.infStrep,
          () => (this as any).$t('absenceReason.infStrep')
        )
        .on(
          v => v === AbsenceReason.infMycoplasma,
          () => (this as any).$t('absenceReason.infMycoplasma')
        )
        .on(
          v => v === AbsenceReason.infHFMD,
          () => (this as any).$t('absenceReason.infHFMD')
        )
        .on(
          v => v === AbsenceReason.infSlappedCheek,
          () => (this as any).$t('absenceReason.infSlappedCheek')
        )
        .on(
          v => v === AbsenceReason.infStomach,
          () => (this as any).$t('absenceReason.infStomach')
        )
        .on(
          v => v === AbsenceReason.infHerpangina,
          () => (this as any).$t('absenceReason.infHerpangina')
        )
        .on(
          v => v === AbsenceReason.infRS,
          () => (this as any).$t('absenceReason.infRS')
        )
        .on(
          v => v === AbsenceReason.infZoster,
          () => (this as any).$t('absenceReason.infZoster')
        )
        .on(
          v => v === AbsenceReason.infRoseola,
          () => (this as any).$t('absenceReason.infRoseola')
        )
        .on(
          v => v === AbsenceReason.infImpetigo,
          () => (this as any).$t('absenceReason.infImpetigo')
        )
        .on(
          v => v === AbsenceReason.covid19,
          () => (this as any).$t('absenceReason.covid19')
        )
        .on(
          v => v === AbsenceReason.others,
          () => (this as any).$t('absenceReason.others')
        )
        .otherwise(() => '');
      return temp;
    },
    close() {
      this.$emit('close');
    },
    isDeleteAbsence(absence: NurseryAttendanceResponse) {
      this.absence = absence;
      this.deleteDialog = true;
    },
    async deleteAbsence() {
      try {
        this.deleteLoading = true;
        if (this.absence) {
          await chatModule.deleteChatsAbsence({
            childId: this.childId,
            date: this.absence.date,
          });
          this.$emit('posted');
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.$emit('close');
        this.deleteLoading = false;
      }
    },
  },

  async mounted() {},
});
</script>
<style lang="scss" scoped>
.delete-absence-dialog {
  z-index: 20 !important;
}
</style>
